import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'app/utils/helpers'
import {useAppDispatch, useAppSelector} from 'setup/redux/Hooks'
import {selectCityData, userActions} from 'app/modules/users/redux/UserSlice'
import {
  FORM_ICONS,
  FieldLabel,
  TextField,
  VatTreatment,
  VatTreatmentField,
} from './ServiceFormFields'
import './style.scss'

export type CityPricingModel = 'specified' | 'unspecified'

export interface CityPriceConfig {
  cityId: string
  cityNameEn: string
  cityNameAr: string
  pricingModel: CityPricingModel
  price?: number
}

interface AdvancedPricingSettingsModalProps {
  isShow: boolean
  onHide: () => void
  isB8akService: boolean
  vatTreatment: VatTreatment
  value: CityPriceConfig[]
  onSave: (value: CityPriceConfig[]) => void
}

const AdvancedPricingSettingsModal: React.FC<AdvancedPricingSettingsModalProps> = ({
  isShow,
  onHide,
  isB8akService,
  vatTreatment,
  value,
  onSave,
}) => {
  const {t, i18n} = useTranslation()
  const isArabic = i18n.language?.toLowerCase().startsWith('ar')
  const dispatch = useAppDispatch()
  const cities = useAppSelector(selectCityData)
  const [cityConfigs, setCityConfigs] = useState<CityPriceConfig[]>(value)
  const [cityToAdd, setCityToAdd] = useState('')

  useEffect(() => {
    if (!isShow) return
    setCityConfigs(value)
    setCityToAdd('')
    dispatch(userActions.fetchCityData())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow])

  const availableCities = useMemo(
    () =>
      (cities || []).filter(
        (city) => !cityConfigs.some((config) => config.cityId === String(city.id))
      ),
    [cities, cityConfigs]
  )

  const addCity = (cityId: string) => {
    // City.id is typed as string but the backend (CityDTO.Id) returns an int,
    // so comparisons must coerce both sides - a raw === here never matches.
    const city = cities.find((candidate) => String(candidate.id) === cityId)
    if (!city) return
    setCityConfigs((current) => [
      ...current,
      {
        cityId: String(city.id),
        cityNameEn: city.nameEn,
        cityNameAr: city.nameAr,
        pricingModel: 'unspecified',
        price: undefined,
      },
    ])
    setCityToAdd('')
  }

  const removeCity = (cityId: string) => {
    setCityConfigs((current) => current.filter((config) => config.cityId !== cityId))
  }

  const setPricingModel = (cityId: string, model: CityPricingModel) => {
    setCityConfigs((current) =>
      current.map((config) =>
        config.cityId === cityId
          ? {
              ...config,
              pricingModel: model,
              price: model === 'unspecified' ? undefined : config.price,
            }
          : config
      )
    )
  }

  const setPrice = (cityId: string, raw: string) => {
    const text = raw.replace(/[^0-9.]/g, '')
    const parsed = text === '' ? undefined : Number(text)
    setCityConfigs((current) =>
      current.map((config) =>
        config.cityId === cityId
          ? {...config, price: parsed === undefined || Number.isNaN(parsed) ? undefined : parsed}
          : config
      )
    )
  }

  const priceRangeText = useMemo(() => {
    const specifiedPrices = cityConfigs
      .filter((config) => config.pricingModel === 'specified' && typeof config.price === 'number')
      .map((config) => config.price as number)
    if (specifiedPrices.length === 0) return ''
    const sar = t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAR')
    const min = Math.min(...specifiedPrices)
    const max = Math.max(...specifiedPrices)
    return min === max ? `${min} ${sar}` : `${min}–${max} ${sar}`
  }, [cityConfigs, t])

  const handleSave = () => {
    onSave(cityConfigs)
    onHide()
  }

  return (
    <Modal
      show={isShow}
      onHide={onHide}
      backdrop='static'
      keyboard={false}
      centered
      dialogClassName='service-form__dialog'
      contentClassName='service-form__content'
    >
      <div className='service-form'>
        <div className='service-form__header'>
          <h2 className='service-form__title'>
            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ADVANCED_PRICING')}
          </h2>
          <button
            type='button'
            className='service-form__close'
            aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CLOSE')}
            onClick={onHide}
          >
            <KTSVG defaultColor={true} path={FORM_ICONS.close} />
          </button>
        </div>

        <div className='service-form__body'>
          {isB8akService && (
            <div className='service-form__banner'>
              <span className='service-form__banner-icon'>
                <KTSVG defaultColor={true} path={FORM_ICONS.info} />
              </span>
              <p className='service-form__banner-text'>
                {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ADVANCED_PRICING_B8AK_NOTICE')}
              </p>
            </div>
          )}

          {isB8akService ? (
            cityConfigs.length === 0 ? (
              <p className='service-form__hint'>
                {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ADVANCED_PRICING_B8AK_NOT_LOADED')}
              </p>
            ) : (
              <div className='service-form__field service-form__field--full'>
                <FieldLabel
                  label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CITY_PRICING_READONLY_TITLE')}
                />
                <div className='service-form__city-pricing-list'>
                  {cityConfigs.map((config) => (
                    <div key={config.cityId} className='service-form__city-pricing-row'>
                      <p className='service-form__city-pricing-name'>
                        {isArabic ? config.cityNameAr : config.cityNameEn}
                      </p>
                      <p className='service-form__hint'>
                        {t(
                          config.pricingModel === 'specified'
                            ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SPECIFIED_PRICING'
                            : 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.UNSPECIFIED_PRICING'
                        )}
                      </p>
                      {config.pricingModel === 'specified' && config.price !== undefined && (
                        <p className='service-form__city-pricing-name'>
                          {t(
                            vatTreatment === 'incl'
                              ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VAT_INCL'
                              : 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VAT_EXCL'
                          )}
                          {' · '}
                          {config.price} {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAR')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {priceRangeText && (
                  <p className='service-form__hint' style={{marginTop: '12px'}}>
                    {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PRICE_RANGE_PREVIEW', {
                      range: priceRangeText,
                    })}
                  </p>
                )}
              </div>
            )
          ) : (
            <>
              <div className='service-form__field service-form__field--full'>
                <FieldLabel label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.FILTER_BY_CITY')} />
                <div className='service-form__select service-form__select--full'>
                  <select
                    className='service-form__select-input'
                    value={cityToAdd}
                    onChange={(event) => addCity(event.target.value)}
                  >
                    <option value='' disabled>
                      {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SELECT_CITY_PLACEHOLDER')}
                    </option>
                    {availableCities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {isArabic ? city.nameAr : city.nameEn}
                      </option>
                    ))}
                  </select>
                  <span className='service-form__select-chevron'>
                    <KTSVG defaultColor={true} path={FORM_ICONS.chevronDown} />
                  </span>
                </div>
                <div className='service-form__city-chips'>
                  {cityConfigs.length === 0 && (
                    <p className='service-form__hint'>
                      {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.NO_CITIES_SELECTED')}
                    </p>
                  )}
                  {cityConfigs.map((config) => (
                    <span key={config.cityId} className='service-form__city-chip'>
                      {isArabic ? config.cityNameAr : config.cityNameEn}
                      <button
                        type='button'
                        className='service-form__city-chip-remove'
                        aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.REMOVE_CITY')}
                        onClick={() => removeCity(config.cityId)}
                      >
                        <KTSVG defaultColor={true} path={FORM_ICONS.close} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {cityConfigs.length > 0 && (
                <div
                  className='service-form__field service-form__field--full'
                  style={{marginTop: '20px'}}
                >
                  <FieldLabel
                    label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CUSTOM_CITY_PRICING')}
                  />
                  <div className='service-form__city-pricing-list'>
                    {cityConfigs.map((config) => (
                      <div key={config.cityId} className='service-form__city-pricing-row'>
                        <p className='service-form__city-pricing-name'>
                          {isArabic ? config.cityNameAr : config.cityNameEn}
                        </p>
                        <div className='service-form__radio-group' role='radiogroup'>
                          <label className='service-form__radio'>
                            <input
                              type='radio'
                              name={`pricing-model-${config.cityId}`}
                              checked={config.pricingModel === 'specified'}
                              onChange={() => setPricingModel(config.cityId, 'specified')}
                            />
                            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SPECIFIED_PRICING')}
                          </label>
                          <label className='service-form__radio'>
                            <input
                              type='radio'
                              name={`pricing-model-${config.cityId}`}
                              checked={config.pricingModel === 'unspecified'}
                              onChange={() => setPricingModel(config.cityId, 'unspecified')}
                            />
                            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.UNSPECIFIED_PRICING')}
                          </label>
                        </div>
                        {config.pricingModel === 'specified' && (
                          <div className='service-form__grid'>
                            <VatTreatmentField
                              value={vatTreatment}
                              disabled
                              onChange={() => undefined}
                            />
                            <TextField
                              id={`city-price-${config.cityId}`}
                              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PRICE')}
                              placeholder='80'
                              suffix={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAR')}
                              value={config.price === undefined ? '' : String(config.price)}
                              onChange={(text) => setPrice(config.cityId, text)}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {priceRangeText && (
                    <p className='service-form__hint' style={{marginTop: '12px'}}>
                      {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PRICE_RANGE_PREVIEW', {
                        range: priceRangeText,
                      })}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className='service-form__footer'>
          <button
            type='button'
            className='service-form__btn service-form__btn--cancel'
            onClick={onHide}
          >
            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CLOSE')}
          </button>
          {!isB8akService && (
            <button
              type='button'
              className='service-form__btn service-form__btn--save'
              onClick={handleSave}
            >
              {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAVE')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default AdvancedPricingSettingsModal
